export const CHART_PERIODS = [
  {
    id: "week",
    label: "Last 7 days",
    shortLabel: "7 days",
    days: 7,
    granularity: "day",
  },
  {
    id: "month",
    label: "Last 30 days",
    shortLabel: "30 days",
    days: 30,
    granularity: "day",
  },
  {
    id: "year",
    label: "Last 12 months",
    shortLabel: "12 months",
    granularity: "month",
  },
];

export const CHART_FIXED_HEIGHT = 260;
export const CHART_WIDGET_HEIGHT = 430;

export const CHART_COLORS = [
  "#4B6CB7",
  "#8B5CF6",
  "#3B82F6",
  "#06B6D4",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#6366F1",
  "#14B8A6",
  "#F97316",
  "#A855F7",
  "#0EA5E9",
];

export const colorAt = (index) => CHART_COLORS[index % CHART_COLORS.length];

export const toLocalDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** ISO week start (Monday) to align with PostgreSQL DATE_TRUNC('week'). */
export const getWeekStartDate = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

export const getChartDateRange = (periodId) => {
  const preset =
    CHART_PERIODS.find((p) => p.id === periodId) || CHART_PERIODS[0];
  const to = new Date();
  const from = new Date();

  if (preset.id === "year") {
    from.setMonth(from.getMonth() - 11);
    from.setDate(1);
  } else {
    from.setDate(from.getDate() - (preset.days || 7));
  }

  return {
    from: toLocalDateString(from),
    to: toLocalDateString(to),
    granularity: preset.granularity || "day",
  };
};

export const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const formatAud = (value, digits = 4) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "$0.00";
  return `$${number.toFixed(digits)}`;
};

/** Compact AUD for chart axes and tooltips (micro spend). */
export const formatAudSmart = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "$0";
  const abs = Math.abs(number);
  if (abs === 0) return "$0";
  if (abs < 0.01) return `$${number.toFixed(4)}`;
  if (abs < 1) return `$${number.toFixed(3)}`;
  if (abs < 100) return `$${number.toFixed(2)}`;
  return `$${number.toFixed(2)}`;
};

const trimFloat = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(1)));
};

const withThousands = (value) =>
  String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** Compact, always-prefixed AUD labels so the $ never clips off the axis. */
export const formatAudAxis = (value, compact = false) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return "$0";
  const abs = Math.abs(number);
  if (abs >= 1000) {
    return `$${trimFloat(number / 1000)}k`;
  }
  if (abs >= 1 && Math.abs(number - Math.round(number)) < 1e-8) {
    return `$${withThousands(number)}`;
  }
  if (abs < 0.01) return `$${number.toFixed(compact ? 3 : 4)}`;
  if (abs < 1) return `$${number.toFixed(2)}`;
  return `$${number.toFixed(2)}`;
};

/** Count labels that stay short on narrow charts (2.5k) and readable on wide ones. */
export const formatCountAxis = (value, compact = false) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${trimFloat(n / 1_000_000)}M`;
  if (abs >= 1000 && (compact || abs >= 10_000)) {
    return `${trimFloat(n / 1000)}k`;
  }
  return withThousands(n);
};

export const formatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString();
};

export const formatPeriodLabel = (period, granularity = "day", options = {}) => {
  if (!period) return "";
  const d = new Date(`${period}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(period);
  if (granularity === "month") {
    const month = d.toLocaleDateString("en-AU", { month: "short" });
    const year = String(d.getFullYear()).slice(-2);
    if (options.shortMonth) {
      return `${month} '${year}`;
    }
    return `${month} '${year}`;
  }
  if (granularity === "week") {
    const weekEnd = new Date(d);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startLabel = d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    const endLabel = weekEnd.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
    return `${startLabel} – ${endLabel}`;
  }
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

export const formatDateRangeDisplay = (from, to) => {
  if (!from || !to) return "";
  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${from} – ${to}`;
  }
  const opts = { day: "numeric", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-AU", opts)} – ${end.toLocaleDateString("en-AU", opts)}`;
};

export const truncateLabel = (text, max = 22) => {
  const value = String(text || "");
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
};

export const percentOf = (part, total, digits = 1) => {
  const p = Number(part);
  const t = Number(total);
  if (!Number.isFinite(p) || !Number.isFinite(t) || t <= 0) return "0%";
  return `${((p / t) * 100).toFixed(digits)}%`;
};

/** Every bucket in [from, to] for the chart x-axis (includes days/months with zero usage). */
export const generatePeriodBuckets = (from, to, granularity = "day") => {
  if (!from || !to) return [];

  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const buckets = [];

  if (granularity === "month") {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= endMonth) {
      buckets.push(toLocalDateString(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return buckets;
  }

  if (granularity === "week") {
    const cursor = getWeekStartDate(start);
    const endWeek = getWeekStartDate(end);
    while (cursor <= endWeek) {
      buckets.push(toLocalDateString(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
    return buckets;
  }

  const cursor = new Date(start);
  while (cursor <= end) {
    buckets.push(toLocalDateString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return buckets;
};

export const fillCostSeriesRange = (series, from, to, granularity = "day") => {
  const byPeriod = new Map(
    (series || []).map((row) => [String(row.period), row])
  );

  return generatePeriodBuckets(from, to, granularity).map((period) => {
    const existing = byPeriod.get(period);
    return {
      period,
      cost_aud: existing ? Number(existing.cost_aud) || 0 : 0,
      call_count: existing ? Number(existing.call_count) || 0 : 0,
    };
  });
};

/** Classic "nice number" step so axis ticks land on 1 / 2 / 5 × 10^n. */
export const niceNumber = (value, round = true) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  const exponent = Math.floor(Math.log10(n));
  const magnitude = 10 ** exponent;
  const fraction = n / magnitude;
  let niceFraction;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) {
    niceFraction = 1;
  } else if (fraction <= 2) {
    niceFraction = 2;
  } else if (fraction <= 5) {
    niceFraction = 5;
  } else {
    niceFraction = 10;
  }
  return niceFraction * magnitude;
};

const roundToStep = (value, step) => {
  if (!step) return value;
  const decimals = ((String(step).split(".")[1] || "").match(/[1-9]/)
    ? String(step).split(".")[1].length
    : 0);
  return Number((Math.round(value / step) * step).toFixed(Math.min(decimals, 8)));
};

/**
 * Even Y-axis scale: nice max, even steps, and 4–6 ticks including 0.
 * Example: peak 2,011 → max 2,500 ticks [0, 500, 1,000, 1,500, 2,000, 2,500].
 */
export const getNiceAxisScale = (values = [], options = {}) => {
  const {
    targetTickCount = 5,
    padRatio = 1.12,
    integer = false,
    emptyMax = integer ? 4 : 1,
  } = options;

  const peak = (values || []).reduce(
    (max, value) => Math.max(max, Number(value) || 0),
    0
  );

  if (peak <= 0) {
    const step = integer ? 1 : emptyMax / 4;
    const ticks = Array.from({ length: 5 }, (_, index) =>
      integer ? index : roundToStep(step * index, step)
    );
    return { max: emptyMax, step, ticks };
  }

  const padded = peak * padRatio;
  let step = niceNumber(padded / Math.max(1, targetTickCount - 1), true);
  if (integer) {
    step = Math.max(1, Math.round(step));
  }

  let max = Math.ceil(padded / step) * step;
  let generated = Math.round(max / step) + 1;
  if (generated > 6) {
    step = integer
      ? Math.max(1, Math.round(niceNumber(step * 2, true)))
      : niceNumber(step * 2, true);
    max = Math.ceil(padded / step) * step;
  }

  const ticks = [];
  for (let current = 0; current <= max + step / 1000; current += step) {
    ticks.push(integer ? Math.round(current) : roundToStep(current, step));
  }
  const last = ticks[ticks.length - 1];
  if (last < max) {
    ticks.push(integer ? Math.round(max) : roundToStep(max, step));
  }

  return { max: ticks[ticks.length - 1], step, ticks };
};

export const getYAxisWidth = (ticks = [], formatter = String, options = {}) => {
  const min = options.min ?? 44;
  const max = options.max ?? 72;
  const pad = options.pad ?? 16;
  const longest = ticks.reduce((len, tick) => {
    return Math.max(len, String(formatter(tick) ?? "").length);
  }, 1);
  return Math.min(max, Math.max(min, longest * 7 + pad));
};

export const getCountAxisScale = (values = [], compact = false) => {
  const scale = getNiceAxisScale(values, { integer: true, padRatio: 1.12 });
  const tickFormatter = (value) => formatCountAxis(value, compact);
  return {
    ...scale,
    width: getYAxisWidth(scale.ticks, tickFormatter, { min: compact ? 36 : 44 }),
    tickFormatter,
  };
};

export const getCostAxisScale = (values = [], compact = false) => {
  const scale = getNiceAxisScale(values, { integer: false, padRatio: 1.15 });
  const tickFormatter = (value) => formatAudAxis(value, compact);
  return {
    ...scale,
    width: getYAxisWidth(scale.ticks, tickFormatter, { min: compact ? 40 : 48 }),
    tickFormatter,
  };
};

export const getChartYAxisProps = (scale, options = {}) => {
  const fontSize = options.fontSize ?? (options.compact ? 10 : 11);
  return {
    type: "number",
    domain: [0, scale.max],
    ticks: scale.ticks,
    tickFormatter: scale.tickFormatter,
    allowDecimals: options.allowDecimals ?? scale.step < 1,
    interval: 0,
    width: scale.width,
    axisLine: false,
    tickLine: false,
    tickMargin: 8,
    minTickGap: 0,
    padding: { top: 0, bottom: 0 },
    tick: { fontSize, fill: "#9ca3af" },
  };
};

export const getChartPlotMargin = ({
  angledXAxis = false,
  extraRight = 12,
} = {}) => ({
  top: 16,
  right: extraRight,
  left: 8,
  bottom: angledXAxis ? 8 : 4,
});

/** Y-axis ceiling with even headroom so bars/lines do not fill the full chart height. */
export const getCostChartYMax = (values = []) => getCostAxisScale(values).max;

/** Thin x-axis labels for dense daily ranges; monthly views show every bucket. */
export const getCostChartTickInterval = (bucketCount, granularity = "day") => {
  if (granularity === "month") return 0;
  if (granularity === "week") {
    if (bucketCount <= 8) return 0;
    return Math.ceil(bucketCount / 6) - 1;
  }
  if (bucketCount <= 8) return 0;
  if (bucketCount <= 16) return 1;
  if (bucketCount <= 31) return Math.ceil(bucketCount / 7) - 1;
  return Math.ceil(bucketCount / 6) - 1;
};

export const getFeatureColumnWidth = (count) => {
  if (count <= 1) return 56;
  if (count <= 3) return 44;
  if (count <= 6) return 36;
  return 28;
};

export const getFeatureYMax = (values = []) => getCountAxisScale(values).max;
